import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  ExternalLink,
  Phone,
  PlayCircle,
  RefreshCw,
  Repeat,
  UserX,
  Video,
  VideoOff,
  XCircle,
} from 'lucide-react';
import { db } from '../firebase';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop, useReportCardMode } from '../components/crm/reportCards';
import {
  GLASS_TEXT,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_SECONDARY_BUTTON,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassBadge,
  GlassAvatar,
  GlassAccentButton,
  GlassSearch,
  GlassSelect,
  GlassEmpty,
  SlidingPills,
} from '../components/glass/GlassUi';
import { TASK_TIME_DEPARTMENTS, taskTimeDepartmentLabel } from '../utils/taskTimeConstants';
import {
  CALL_STATUS,
  LOG_STATUS,
  RECORDING_STATUS,
  groupCallsByAdvisor,
  groupCallsByClient,
  internalUserName,
  loadCallReport,
  needsAttention,
  summarizeCalls,
} from '../services/callReportsService';

const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 75;
const COMING_UP_DAYS = 14;

const TIMEFRAMES = [
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'last_30', label: 'Last 30 Days' },
  { id: 'last_90', label: 'Last 90 Days' },
  { id: 'next_30', label: 'Next 30 Days' },
  { id: 'last_6m', label: 'Last 6 Months' },
  { id: 'ytd', label: 'This Year' },
  { id: 'last_12m', label: 'Last 12 Months' },
];

function getTimeframeRange(id, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (id) {
    case 'this_month':
      return { start: new Date(y, m, 1), end: new Date(y, m + 1, 1) };
    case 'last_month':
      return { start: new Date(y, m - 1, 1), end: new Date(y, m, 1) };
    case 'last_30':
      return { start: new Date(now.getTime() - 30 * DAY_MS), end: now };
    case 'next_30':
      return { start: now, end: new Date(now.getTime() + 30 * DAY_MS) };
    case 'last_6m':
      return { start: new Date(y, m - 5, 1), end: now };
    case 'ytd':
      return { start: new Date(y, 0, 1), end: now };
    case 'last_12m':
      return { start: new Date(now.getTime() - 365 * DAY_MS), end: now };
    case 'last_90':
    default:
      return { start: new Date(now.getTime() - 90 * DAY_MS), end: now };
  }
}

// Every fetch also covers the next 30 days so "Coming up" works on any timeframe.
function getFetchWindow(range, now = new Date()) {
  const ahead = new Date(now.getTime() + 30 * DAY_MS);
  return { start: range.start, end: range.end > ahead ? range.end : ahead };
}

// Glass badge tones: mint = held, blue = upcoming, red = canceled, amber = moved, coral = no-show
const STATUS_META = {
  [CALL_STATUS.COMPLETED]: { label: 'Completed', tone: 'mint' },
  [CALL_STATUS.UPCOMING]: { label: 'Upcoming', tone: 'blue' },
  [CALL_STATUS.CANCELED]: { label: 'Canceled', tone: 'red' },
  [CALL_STATUS.RESCHEDULED]: { label: 'Rescheduled', tone: 'amber' },
  [CALL_STATUS.NO_SHOW]: { label: 'No-show', tone: 'coral' },
};

const SOURCE_META = {
  calendly: { label: 'Calendly', tone: 'blue' },
  google: { label: 'Google Calendar', tone: 'mint' },
  crm: { label: 'Hours log', tone: 'neutral' },
  fathom: { label: 'Fathom', tone: 'lavender' },
};

const MATCHED_BY_LABEL = {
  email: 'invitee email',
  domain: 'company email domain',
  name: 'invitee name',
  calendar: 'calendar invite',
  fathom: 'Fathom recording',
  crm: 'hours log',
};

// Soft pill tones for pills that carry an icon or tooltip (GlassBadge has neither); spelled out for the JIT
const PILL = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap';
const PILL_TONES = {
  red: 'bg-[#F80707]/10 text-[#D10606] dark:bg-[#F80707]/[0.18] dark:text-[#FF6B5E]',
  amber: 'bg-[#FFB547]/20 text-[#94600A] dark:bg-[#FFB547]/[0.15] dark:text-[#FFB547]',
  lavender: 'bg-[#B9A3F5]/25 text-[#5B43A8] dark:bg-[#B9A3F5]/[0.15] dark:text-[#B9A3F5]',
  neutral: 'bg-black/[0.05] text-gray-600 dark:bg-white/[0.06] dark:text-white/60',
};

const MUTED = 'text-gray-500 dark:text-white/50';
const FAINT = 'text-gray-400 dark:text-white/30';
const LAVENDER_TEXT = 'text-[#6B4FC8] dark:text-[#B9A3F5]';
const LINK = 'font-medium text-[#E00606] hover:underline dark:text-[#FF4D40]';
const SMALL_SECONDARY_BUTTON = 'inline-flex items-center gap-1 rounded-full border border-black/10 bg-white/40 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:border-[#F80707]/40 hover:text-[#F80707] dark:border-white/10 dark:bg-white/[0.04] dark:text-white/75 dark:hover:border-[#F80707]/50 dark:hover:text-[#FF4D40]';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function fmtWhen(date, timeKnown = true) {
  if (!date) return '—';
  const day = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
  if (!timeKnown) return day;
  return `${day} · ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

function fmtShortDate(date) {
  if (!date) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
}

function fmtMinutes(minutes) {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtHours(hours) {
  const rounded = Math.round(hours * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}h`;
}

function relativeDays(date, now = new Date()) {
  if (!date) return '';
  const days = Math.round((date - now) / DAY_MS);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

const IconPill = ({ tone = 'neutral', icon: Icon, title, children }) => (
  <span className={`${PILL} ${PILL_TONES[tone] || PILL_TONES.neutral}`} title={title}>
    {Icon && <Icon className="h-3 w-3" />}
    {children}
  </span>
);

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META[CALL_STATUS.COMPLETED];
  return <GlassBadge tone={meta.tone}>{meta.label}</GlassBadge>;
};

const LogPill = ({ call }) => {
  switch (call.logStatus) {
    case LOG_STATUS.LOGGED:
      return <GlassBadge tone="mint">Logged · {fmtHours(call.log.quantity)}</GlassBadge>;
    case LOG_STATUS.NOT_LOGGED:
      return (
        <IconPill tone="amber" icon={AlertTriangle}>
          Not logged
        </IconPill>
      );
    case LOG_STATUS.LOGGED_CANCELED:
      return (
        <IconPill tone="red" icon={AlertTriangle}>
          Still logged
        </IconPill>
      );
    case LOG_STATUS.UNMATCHED:
      return <GlassBadge tone="neutral">No client match</GlassBadge>;
    default:
      return <span className={`text-xs ${FAINT}`}>—</span>;
  }
};

const RecordingPill = ({ call }) => {
  switch (call.recordingStatus) {
    case RECORDING_STATUS.RECORDED: {
      const pill = (
        <IconPill tone="lavender" icon={PlayCircle}>
          Recorded
        </IconPill>
      );
      return call.recording?.url ? (
        <a href={call.recording.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Watch Fathom recording">
          {pill}
        </a>
      ) : pill;
    }
    case RECORDING_STATUS.NOT_RECORDED:
      return (
        <IconPill
          tone="red"
          icon={VideoOff}
          title={call.host?.fathomConnected === false ? `${call.host.name} hasn't connected Fathom` : 'No Fathom recording found'}
        >
          Not recorded
        </IconPill>
      );
    case RECORDING_STATUS.PENDING:
      return <IconPill tone="neutral" title="Fathom usually sends the recording within an hour of the call">Processing</IconPill>;
    default:
      return <span className={`text-xs ${FAINT}`}>—</span>;
  }
};

const FathomNotConnectedPill = () => (
  <GlassBadge tone="amber">
    Fathom not connected
  </GlassBadge>
);

const SourceChips = ({ sources }) => (
  <div className="flex flex-wrap gap-1.5">
    {(sources || []).map((s) => (
      <GlassBadge key={s} tone={SOURCE_META[s]?.tone || SOURCE_META.crm.tone}>{SOURCE_META[s]?.label || s}</GlassBadge>
    ))}
  </div>
);

const Avatar = ({ host, size = 'h-7 w-7' }) => {
  const name = host?.name || 'Unassigned';
  const initials = name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase() || '?';
  if (host?.profileImage) {
    return <img src={host.profileImage} alt="" className={`${size} shrink-0 rounded-full object-cover`} />;
  }
  // Default size uses the shared pastel initials circle; the tiny Coming-up avatar stays neutral
  if (size === 'h-7 w-7') return <GlassAvatar name={host?.name || ''} />;
  return (
    <span className={`${size} shrink-0 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-[9px] font-semibold text-gray-600 dark:bg-white/[0.08] dark:text-white/70`}>
      {initials}
    </span>
  );
};

const HostCell = ({ host }) => (
  <div className="flex items-center gap-2 min-w-0">
    <Avatar host={host} />
    <span className="truncate text-sm text-gray-700 dark:text-white/75">{host?.name || 'Unassigned'}</span>
  </div>
);

const IconLink = ({ href, title, icon: Icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={title}
    onClick={(e) => e.stopPropagation()}
    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-[#F80707]/[0.06] hover:text-[#F80707] dark:text-white/50 dark:hover:bg-[#F80707]/10 dark:hover:text-[#FF4D40]"
  >
    <Icon className="h-4 w-4" />
  </a>
);

const CallLinks = ({ call }) => (
  <div className="flex items-center justify-end gap-0.5">
    {call.joinUrl && call.status === CALL_STATUS.UPCOMING && <IconLink href={call.joinUrl} title="Join call" icon={Video} />}
    {call.googleHtmlLink && <IconLink href={call.googleHtmlLink} title="Open in Google Calendar" icon={CalendarDays} />}
    {call.recording?.url && <IconLink href={call.recording.url} title="Watch Fathom recording" icon={PlayCircle} />}
  </div>
);

const ClientCell = ({ call, onOpenClient }) => {
  const inviteeLabel = call.invitee?.name || call.invitee?.email || '';
  if (call.accountId) {
    return (
      <div className="min-w-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenClient(call.accountId);
          }}
          className="block max-w-full truncate text-left text-sm font-medium text-gray-900 transition hover:text-[#F80707] dark:text-white dark:hover:text-[#FF4D40]"
        >
          {call.accountName || 'Client'}
        </button>
        {inviteeLabel && <div className={`truncate text-xs ${MUTED}`}>{inviteeLabel}</div>}
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <div className="truncate text-sm font-medium text-gray-700 dark:text-white/75">{inviteeLabel || 'Unknown invitee'}</div>
      {call.invitee?.name && call.invitee?.email && (
        <div className={`truncate text-xs ${MUTED}`}>{call.invitee.email}</div>
      )}
    </div>
  );
};

const DetailBlock = ({ title, children }) => (
  <div className="min-w-0">
    <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-white/40">{title}</div>
    <div className="space-y-1 text-sm text-gray-700 dark:text-white/75">{children}</div>
  </div>
);

function logExplanation(call) {
  switch (call.logStatus) {
    case LOG_STATUS.NOT_LOGGED:
      return call.status === CALL_STATUS.UPCOMING
        ? 'Booked, but nothing is on the client\'s Implementation Hours log yet.'
        : 'This call happened but isn\'t on the client\'s Implementation Hours log, so no hours were deducted.';
    case LOG_STATUS.LOGGED_CANCELED:
      return 'This call was canceled but its log entry is still on Implementation Hours — it still counts against the client\'s hours.';
    case LOG_STATUS.UNMATCHED:
      return 'The invitee doesn\'t match any client contact, portal user or company email domain.';
    default:
      return call.kind === 'meeting' ? 'Not an Implementation Hours call type.' : 'Nothing logged (canceled calls don\'t need a log).';
  }
}

const CallDetail = ({ call }) => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
    <DetailBlock title="Who">
      {call.invitees.length === 0 && <div className={MUTED}>No invitee details</div>}
      {call.invitees.map((i) => (
        <div key={`${i.email}-${i.name}`} className="truncate">
          {i.name ? <span className="font-medium">{i.name}</span> : null}
          {i.name && i.email ? ' · ' : ''}
          {i.email && <span className={MUTED}>{i.email}</span>}
        </div>
      ))}
      <div className={MUTED}>Advisor: {call.host?.name || 'Unassigned'}</div>
      {call.matchedBy && call.accountId && (
        <div className={`text-xs ${MUTED}`}>Matched to client by {MATCHED_BY_LABEL[call.matchedBy] || call.matchedBy}</div>
      )}
    </DetailBlock>

    <DetailBlock title="Booking">
      <div>{fmtWhen(call.start, call.timeKnown)}{call.durationMinutes ? ` · ${fmtMinutes(call.durationMinutes)}` : ''}</div>
      {call.bookedAt && <div className={MUTED}>Booked {fmtShortDate(call.bookedAt)}</div>}
      {call.department && <div className={MUTED}>{taskTimeDepartmentLabel(call.department)} department</div>}
      <div className="pt-1"><SourceChips sources={call.sources} /></div>
      {call.joinUrl && (
        <a href={call.joinUrl} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 text-xs ${LINK}`}>
          Meeting link <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </DetailBlock>

    <DetailBlock title="Implementation Hours">
      {call.log ? (
        <>
          <div>
            Logged {fmtHours(call.log.quantity)} on {call.log.dateKey || fmtShortDate(call.log.when)}
          </div>
          {call.log.meetingStatus && <div className={MUTED}>Log status: {call.log.meetingStatus}</div>}
          {call.logStatus === LOG_STATUS.LOGGED_CANCELED && <div className={GLASS_TEXT.red}>{logExplanation(call)}</div>}
        </>
      ) : (
        <div className={call.logStatus === LOG_STATUS.NOT_LOGGED || call.logStatus === LOG_STATUS.UNMATCHED ? GLASS_TEXT.amber : MUTED}>
          {logExplanation(call)}
        </div>
      )}
    </DetailBlock>

    {call.cancellation && (
      <DetailBlock title={call.status === CALL_STATUS.RESCHEDULED ? 'Rescheduled' : 'Canceled'}>
        <div>
          {call.status === CALL_STATUS.RESCHEDULED ? 'Moved' : 'Canceled'}
          {call.cancellation.by ? ` by ${call.cancellation.by}` : ''}
          {call.cancellation.byType ? ` (${call.cancellation.byType})` : ''}
          {call.cancellation.at ? ` on ${fmtShortDate(call.cancellation.at)}` : ''}
        </div>
        {call.cancellation.reason && <div className={MUTED}>“{call.cancellation.reason}”</div>}
      </DetailBlock>
    )}

    {call.notes.length > 0 && (
      <DetailBlock title="Prep notes from invitee">
        {call.notes.map((n, i) => (
          <div key={i} className="whitespace-pre-line">{n.answer}</div>
        ))}
      </DetailBlock>
    )}

    {(call.recordingStatus === RECORDING_STATUS.NOT_RECORDED || call.recordingStatus === RECORDING_STATUS.PENDING) && (
      <DetailBlock title="Fathom recording">
        {call.recordingStatus === RECORDING_STATUS.PENDING ? (
          <div className={MUTED}>This call just ended — Fathom usually sends the recording within an hour.</div>
        ) : (
          <>
            <div className={GLASS_TEXT.red}>No Fathom recording reached the CRM for this call.</div>
            {call.host?.fathomConnected === false && (
              <div className={MUTED}>
                {call.host.name} hasn't connected Fathom in CRM ▸ My Profile, so their recordings can't come through.
              </div>
            )}
          </>
        )}
      </DetailBlock>
    )}

    {call.recording && (
      <DetailBlock title="Fathom recording">
        {call.recording.summary ? (
          <div className="max-h-48 overflow-auto whitespace-pre-line text-gray-600 dark:text-white/70">{call.recording.summary}</div>
        ) : (
          <div className={MUTED}>No summary yet</div>
        )}
        <div className="flex flex-wrap items-center gap-3 pt-1 text-xs">
          {call.recording.actionItemCount > 0 && <span className={MUTED}>{call.recording.actionItemCount} action items</span>}
          {call.recording.url && (
            <a href={call.recording.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 ${LINK}`}>
              Watch recording <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </DetailBlock>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Source status
// ---------------------------------------------------------------------------

const SourceStatusBar = ({ sources, fetchedAt, onConnect }) => {
  if (!sources) return null;
  const { calendly, google, crm, fathom } = sources;
  const chip = (tone) => ({
    ok: 'mint',
    warn: 'amber',
    error: 'red',
    muted: 'neutral',
  }[tone]);

  let calendlyTone = 'ok';
  let calendlyText;
  let calendlyTitle = '';
  if (calendly.state === 'ok' && calendly.connectedUsers.length === 0 && calendly.failedUsers.length > 0) {
    calendlyTone = 'error';
    calendlyText = 'Calendly sign-in failed — reconnect';
    calendlyTitle = calendly.failedUsers.map((u) => `${u.name}: ${u.error}`).join('\n');
  } else if (calendly.state === 'ok') {
    calendlyText = calendly.mode === 'organization'
      ? `Calendly · whole team · ${calendly.eventCount} bookings`
      : `Calendly · ${calendly.connectedUsers.length} calendar${calendly.connectedUsers.length === 1 ? '' : 's'} · ${calendly.eventCount} bookings`;
    calendlyTitle = calendly.mode === 'organization'
      ? `Organization-wide via ${calendly.viaUser?.name || 'a Calendly admin'}`
      : `Connected: ${calendly.connectedUsers.map((u) => u.name).join(', ')}`;
    if (calendly.failedUsers.length) {
      calendlyTone = 'warn';
      calendlyTitle += ` — needs reconnecting: ${calendly.failedUsers.map((u) => u.name).join(', ')}`;
    }
  } else if (calendly.state === 'not_connected') {
    calendlyTone = 'warn';
    calendlyText = 'Calendly not connected';
    calendlyTitle = 'Connect Calendly in CRM ▸ My Profile ▸ Integrations';
  } else {
    calendlyTone = 'error';
    const notDeployed = calendly.code === 'functions/not-found' || calendly.code === 'functions/internal';
    calendlyText = notDeployed ? 'Calendly sync not deployed yet' : 'Calendly sync failed';
    calendlyTitle = calendly.message || '';
  }

  const googleTone = google.connectedUsers.length === 0 ? 'warn' : google.failedUsers.length ? 'warn' : 'ok';
  const googleText = google.connectedUsers.length === 0
    ? 'No team Google Calendars connected'
    : `Google Calendar · ${google.connectedUsers.length} of ${google.totalUsers} teammates`;
  const googleTitle = google.connectedUsers.length
    ? `Connected: ${google.connectedUsers.join(', ')}${google.failedUsers.length ? ` — couldn't load: ${google.failedUsers.join(', ')}` : ''}`
    : 'Teammates connect Google Calendar in CRM ▸ My Profile ▸ Integrations';

  const fathomTone = fathom.state !== 'ok' || fathom.connectedUsers.length === 0 ? 'warn' : 'ok';
  const fathomTitle = fathom.connectedUsers.length
    ? `Recordings come in from: ${fathom.connectedUsers.join(', ')}`
    : 'Teammates connect Fathom in CRM ▸ My Profile ▸ Integrations';

  const calendlyNeedsReconnect = calendly.state === 'ok' && calendly.failedUsers.length > 0;

  return (
    <div className="space-y-3">
    {calendlyNeedsReconnect && (
      <div className="rounded-2xl border border-[#F80707]/20 bg-[#F80707]/[0.06] px-4 py-3 text-sm text-[#B00505] dark:border-[#F80707]/30 dark:bg-[#F80707]/10 dark:text-[#FF6B5E]">
        <div className="font-medium">
          Calendly couldn't sign in for {calendly.failedUsers.map((u) => u.name).join(', ')}
          {calendly.connectedUsers.length === 0 ? ' — no Calendly bookings are included below.' : '.'}
        </div>
        <div className="mt-1 text-xs opacity-80">{calendly.failedUsers[0]?.error}</div>
        <GlassAccentButton onClick={onConnect} className="mt-3">
          Reconnect Calendly in My Profile
        </GlassAccentButton>
      </div>
    )}
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span title={calendlyTitle}>
        <GlassBadge tone={chip(calendlyTone)}>{calendlyText}</GlassBadge>
      </span>
      <span title={googleTitle}>
        <GlassBadge tone={chip(googleTone)}>{googleText}</GlassBadge>
      </span>
      <GlassBadge tone={chip('muted')}>Hours logs · {crm.logCount}</GlassBadge>
      <span title={fathomTitle}>
        <GlassBadge tone={chip(fathomTone)}>
          {fathom.state === 'ok'
            ? `Fathom · ${fathom.recordingCount} recordings · ${fathom.connectedUsers.length} of ${fathom.totalUsers} teammates`
            : 'Fathom unavailable'}
        </GlassBadge>
      </span>
      {(calendly.state === 'not_connected' || google.connectedUsers.length === 0 || fathom.connectedUsers.length === 0) && (
        <button type="button" onClick={onConnect} className={LINK}>
          Connect in My Profile
        </button>
      )}
      {fetchedAt && (
        <span className={FAINT}>
          Synced {fetchedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        </span>
      )}
    </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Quick-stat filter tiles
// ---------------------------------------------------------------------------

const TILE_TEXT = {
  gray: GLASS_TEXT.strong,
  green: GLASS_TEXT.mint,
  blue: GLASS_TEXT.blue,
  red: GLASS_TEXT.red,
  yellow: GLASS_TEXT.amber,
  orange: GLASS_TEXT.coral,
};

const StatTile = ({ icon: Icon, label, value, tone = 'gray', onClick, active }) => {
  const cls = `flex min-w-0 flex-col gap-1.5 rounded-2xl border px-3.5 py-3 text-left transition ${
    active
      ? 'border-[#F80707]/50 bg-[#F80707]/[0.06] dark:border-[#F80707]/60 dark:bg-[#F80707]/10'
      : 'border-black/[0.05] bg-black/[0.03] dark:border-white/[0.06] dark:bg-white/[0.035]'
  } ${onClick ? 'cursor-pointer hover:border-[#F80707]/40 dark:hover:border-[#F80707]/50' : ''}`;
  const body = (
    <>
      <span className={`flex items-center gap-1.5 text-[11px] ${MUTED}`}>
        <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${tone === 'gray' ? GLASS_TEXT.muted : TILE_TEXT[tone]}`} strokeWidth={1.75} />
        <span className="truncate">{label}</span>
      </span>
      <span className={`text-xl font-semibold tabular-nums ${TILE_TEXT[tone] || TILE_TEXT.gray}`}>{value}</span>
    </>
  );
  if (!onClick) return <div className={cls}>{body}</div>;
  return (
    <button type="button" onClick={onClick} aria-pressed={!!active} className={cls}>
      {body}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Tab content
// ---------------------------------------------------------------------------

const ComingUp = ({ calls, onOpenClient }) => {
  // My view keeps the card mounted (with an empty state) so a pinned Coming up always shows
  const cardMode = useReportCardMode();
  if (calls.length === 0 && !cardMode) return null;
  return (
    <GlassCard className="p-5" delay={0}>
      <GlassCardHeader title={`Coming up · next ${COMING_UP_DAYS} days`} />
      {calls.length === 0 && <GlassEmpty className="py-6">No calls in the next {COMING_UP_DAYS} days</GlassEmpty>}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {calls.map((call) => (
          <div key={call.id} className={`${GLASS_INSET} flex items-start gap-3 p-3`}>
            <div className="flex w-12 shrink-0 flex-col items-center rounded-xl border border-black/[0.05] bg-white/70 py-1.5 dark:border-white/[0.06] dark:bg-white/[0.06]">
              <span className="text-[10px] font-semibold uppercase text-[#E00606] dark:text-[#FF4D40]">
                {call.start.toLocaleDateString(undefined, { month: 'short' })}
              </span>
              <span className="text-lg font-semibold leading-none text-gray-900 tabular-nums dark:text-white">{call.start.getDate()}</span>
            </div>
            <div className="min-w-0 flex-1">
              <ClientCell call={call} onOpenClient={onOpenClient} />
              <div className={`mt-1 truncate text-xs ${MUTED}`}>
                {call.start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} · {call.title}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <Avatar host={call.host} size="h-5 w-5" />
                <span className="truncate text-xs text-gray-600 dark:text-white/60">{call.host?.name || 'Unassigned'}</span>
                <span className={`text-xs ${FAINT}`}>· {relativeDays(call.start)}</span>
              </div>
            </div>
            <CallLinks call={call} />
          </div>
        ))}
      </div>
    </GlassCard>
  );
};

const CallsTable = ({ calls, onOpenClient }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => setVisible(PAGE_SIZE), [calls]);

  if (calls.length === 0) {
    return (
      <GlassCard className="p-5" delay={80}>
        <GlassCardHeader title="Calls" />
        <GlassEmpty className="py-10">No calls match these filters</GlassEmpty>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-5" delay={80}>
      <GlassCardHeader title="Calls" />
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[980px] text-sm">
          <thead>
            <tr>
              <th className={`${GLASS_TH} w-8`} />
              <th className={GLASS_TH}>When</th>
              <th className={GLASS_TH}>Client</th>
              <th className={GLASS_TH}>Call</th>
              <th className={GLASS_TH}>Advisor</th>
              <th className={GLASS_TH}>Status</th>
              <th className={GLASS_TH}>Recording</th>
              <th className={GLASS_TH}>Hours log</th>
              <th className={`${GLASS_TH} text-right`}>Links</th>
            </tr>
          </thead>
          <tbody className="text-gray-800 dark:text-white/[0.85]">
            {calls.slice(0, visible).map((call) => {
              const open = expandedId === call.id;
              return (
                <React.Fragment key={call.id}>
                  <tr
                    onClick={() => setExpandedId(open ? null : call.id)}
                    className={`${GLASS_ROW} cursor-pointer ${open ? 'bg-black/[0.025] dark:bg-white/[0.035]' : ''}`}
                  >
                    <td className={`py-3 pr-2 ${FAINT}`}>
                      {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap text-gray-700 dark:text-white/75">{fmtWhen(call.start, call.timeKnown)}</td>
                    <td className="max-w-[220px] py-3 pr-4"><ClientCell call={call} onOpenClient={onOpenClient} /></td>
                    <td className="max-w-[240px] py-3 pr-4">
                      <div className="truncate text-sm text-gray-900 dark:text-white">{call.title}</div>
                      <div className={`truncate text-xs ${MUTED}`}>
                        {[call.department ? taskTimeDepartmentLabel(call.department) : null, call.durationMinutes ? fmtMinutes(call.durationMinutes) : null]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </div>
                    </td>
                    <td className="max-w-[180px] py-3 pr-4"><HostCell host={call.host} /></td>
                    <td className="py-3 pr-4"><StatusPill status={call.status} /></td>
                    <td className="py-3 pr-4"><RecordingPill call={call} /></td>
                    <td className="py-3 pr-4"><LogPill call={call} /></td>
                    <td className="py-3"><CallLinks call={call} /></td>
                  </tr>
                  {open && (
                    <tr className="bg-black/[0.02] dark:bg-white/[0.025]">
                      <td />
                      <td colSpan={8} className="py-5 pr-4">
                        <CallDetail call={call} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {calls.length > visible && (
        <div className="mt-4 border-t border-black/[0.06] pt-4 text-center dark:border-white/[0.06]">
          <button
            type="button"
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className={GLASS_SECONDARY_BUTTON}
          >
            Show more ({calls.length - visible} left)
          </button>
        </div>
      )}
    </GlassCard>
  );
};

const AdvisorTable = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <GlassCard className="p-5">
        <GlassCardHeader title="By Advisor" />
        <GlassEmpty className="py-10">No calls match these filters</GlassEmpty>
      </GlassCard>
    );
  }
  const num = 'py-3 pr-4 text-center tabular-nums';
  return (
    <GlassCard className="p-5">
      <GlassCardHeader title="By Advisor" />
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[1080px] text-sm">
          <thead>
            <tr>
              <th className={GLASS_TH}>Advisor</th>
              {['Calls', 'Completed', 'Upcoming', 'Canceled', 'Rescheduled', 'No-show', 'Clients', 'Hours held', 'Avg call', 'Held rate', 'Recorded', 'Not recorded', 'Record rate'].map((h) => (
                <th key={h} className={`${GLASS_TH} text-center`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-700 dark:text-white/75">
            {rows.map((row) => (
              <tr key={row.id} className={GLASS_ROW}>
                <td className="py-3 pr-4">
                  <HostCell host={row.host} />
                  {row.host?.fathomConnected === false && <div className="mt-1 pl-10"><FathomNotConnectedPill /></div>}
                </td>
                <td className={`${num} font-semibold text-gray-900 dark:text-white`}>{row.total}</td>
                <td className={`${num} ${GLASS_TEXT.mint}`}>{row.completed}</td>
                <td className={`${num} ${GLASS_TEXT.blue}`}>{row.upcoming}</td>
                <td className={`${num} ${GLASS_TEXT.red}`}>{row.canceled}</td>
                <td className={`${num} ${GLASS_TEXT.amber}`}>{row.rescheduled}</td>
                <td className={`${num} ${GLASS_TEXT.coral}`}>{row.noShow}</td>
                <td className={num}>{row.clientCount}</td>
                <td className={num}>{row.heldMinutes ? fmtHours(row.heldMinutes / 60) : '—'}</td>
                <td className={num}>{fmtMinutes(row.avgMinutes)}</td>
                <td className={num}>{row.heldRate == null ? '—' : `${row.heldRate}%`}</td>
                <td className={`${num} ${LAVENDER_TEXT}`}>{row.recorded}</td>
                <td className={`${num} ${row.notRecorded ? `font-semibold ${GLASS_TEXT.red}` : FAINT}`}>{row.notRecorded}</td>
                <td className={`${num} ${row.recordingRate != null && row.recordingRate < 100 ? GLASS_TEXT.red : ''}`}>
                  {row.recordingRate == null ? '—' : `${row.recordingRate}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
};

const ClientTable = ({ rows, onOpenClient }) => {
  if (rows.length === 0) {
    return (
      <GlassCard className="p-5">
        <GlassCardHeader title="By Client" />
        <GlassEmpty className="py-10">No clients match these filters</GlassEmpty>
      </GlassCard>
    );
  }
  const num = 'py-3 pr-4 text-center tabular-nums';
  return (
    <GlassCard className="p-5">
      <GlassCardHeader title="By Client" />
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[880px] text-sm">
          <thead>
            <tr>
              <th className={GLASS_TH}>Client</th>
              {['Completed', 'Upcoming', 'Canceled', 'Hours held', 'Hours logged', 'Last call', 'Next call'].map((h) => (
                <th key={h} className={`${GLASS_TH} text-center`}>{h}</th>
              ))}
              <th className={GLASS_TH}>Flags</th>
            </tr>
          </thead>
          <tbody className="text-gray-700 dark:text-white/75">
            {rows.map((row) => {
              const cold = !row.nextCall && (row.daysSinceLastCall == null || row.daysSinceLastCall > 30);
              return (
                <tr key={row.accountId} className={GLASS_ROW}>
                  <td className="max-w-[240px] py-3 pr-4">
                    <button
                      type="button"
                      onClick={() => onOpenClient(row.accountId)}
                      className="flex max-w-full items-center gap-3 text-left font-medium text-gray-900 transition hover:text-[#F80707] dark:text-white dark:hover:text-[#FF4D40]"
                    >
                      <GlassAvatar name={row.accountName || ''} />
                      <span className="truncate">{row.accountName || 'Client'}</span>
                    </button>
                  </td>
                  <td className={`${num} ${GLASS_TEXT.mint}`}>{row.completed}</td>
                  <td className={`${num} ${GLASS_TEXT.blue}`}>{row.upcoming}</td>
                  <td className={`${num} ${GLASS_TEXT.red}`}>{row.canceled + row.rescheduled + row.noShow}</td>
                  <td className={num}>{row.heldMinutes ? fmtHours(row.heldMinutes / 60) : '—'}</td>
                  <td className={num}>{row.loggedHours ? fmtHours(row.loggedHours) : '—'}</td>
                  <td className={num}>
                    {row.lastCall ? (
                      <>
                        <div>{fmtShortDate(row.lastCall)}</div>
                        <div className={`text-xs ${FAINT}`}>{row.daysSinceLastCall}d ago</div>
                      </>
                    ) : '—'}
                  </td>
                  <td className={num}>
                    {row.nextCall ? (
                      <>
                        <div>{fmtShortDate(row.nextCall)}</div>
                        <div className={`text-xs ${FAINT}`}>{relativeDays(row.nextCall)}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {cold && (
                        <GlassBadge tone="red">
                          {row.lastCall ? 'No call in 30+ days' : 'No calls on record'}
                        </GlassBadge>
                      )}
                      {row.attention > 0 && (
                        <GlassBadge tone="amber">
                          {row.attention} to review
                        </GlassBadge>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
};

const ATTENTION_GROUPS = [
  {
    id: 'not_logged',
    title: 'Held but not logged to Implementation Hours',
    hint: 'No hours were deducted for these calls. Log them from the client\'s Program Deliverables.',
    test: (c) => c.logStatus === LOG_STATUS.NOT_LOGGED && c.status === CALL_STATUS.COMPLETED,
  },
  {
    id: 'not_recorded',
    title: 'Held but not recorded in Fathom',
    hint: 'Every call should be recorded. Make sure the advisor starts Fathom on every call and has Fathom connected in My Profile.',
    test: (c) => c.recordingStatus === RECORDING_STATUS.NOT_RECORDED,
  },
  {
    id: 'logged_canceled',
    title: 'Canceled or rescheduled but still logged',
    hint: 'These log entries still count against the client\'s hours. Remove them from Program Deliverables.',
    test: (c) => c.logStatus === LOG_STATUS.LOGGED_CANCELED,
  },
  {
    id: 'unmatched',
    title: 'Couldn\'t match to a client',
    hint: 'Add the invitee as a portal user or contact on the right client so their calls attach automatically.',
    test: (c) => c.logStatus === LOG_STATUS.UNMATCHED,
  },
];

const AttentionPanel = ({ calls, onOpenClient }) => {
  // My view keeps every group card mounted (even when empty) so a pinned group always shows
  const cardMode = useReportCardMode();
  const groups = ATTENTION_GROUPS.map((g) => ({ ...g, calls: calls.filter(g.test) })).filter((g) => cardMode || g.calls.length > 0);
  if (groups.length === 0) {
    return (
      <GlassCard className="flex flex-col items-center gap-2 py-16 text-center">
        <CheckCircle2 className={`h-8 w-8 ${GLASS_TEXT.mint}`} strokeWidth={1.6} />
        <div className="text-sm font-medium text-gray-900 dark:text-white">Everything reconciles</div>
        <div className={`text-sm ${MUTED}`}>Every call in this range matches the Implementation Hours log and has a Fathom recording.</div>
      </GlassCard>
    );
  }
  return (
    <div className="space-y-4">
      {groups.map((group, idx) => (
        <PinnableCard key={group.id} id={`attention-${group.id.replace(/_/g, '-')}`} title={group.title} size="wide">
        <GlassCard className="p-5" delay={idx * 80}>
          <GlassCardHeader
            title={(
              <>
                {group.title} <span className={FAINT}>({group.calls.length})</span>
              </>
            )}
          />
          <p className={`mt-1 text-xs ${MUTED}`}>{group.hint}</p>
          {group.calls.length === 0 && <GlassEmpty className="py-6">Nothing to review in this range</GlassEmpty>}
          <ul className="mt-4">
            {group.calls.map((call) => (
              <li key={call.id} className={`${GLASS_ROW} flex flex-wrap items-center gap-x-4 gap-y-2 py-3`}>
                <div className="w-40 shrink-0 text-sm text-gray-700 dark:text-white/75">{fmtWhen(call.start, call.timeKnown)}</div>
                <div className="min-w-[160px] flex-1"><ClientCell call={call} onOpenClient={onOpenClient} /></div>
                <div className="min-w-[140px] flex-1 truncate text-sm text-gray-600 dark:text-white/60">{call.title}</div>
                <div className="w-40"><HostCell host={call.host} /></div>
                <StatusPill status={call.status} />
                {group.id === 'not_recorded' && call.host?.fathomConnected === false && <FathomNotConnectedPill />}
                {call.log && <span className={`text-xs ${MUTED}`}>Logged {fmtHours(call.log.quantity)} · {call.log.dateKey}</span>}
                {call.accountId && (
                  <button
                    type="button"
                    onClick={() => onOpenClient(call.accountId)}
                    className={`ml-auto ${SMALL_SECONDARY_BUTTON}`}
                  >
                    Open client <ExternalLink className="h-3 w-3" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </GlassCard>
        </PinnableCard>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'calls', label: 'Calls' },
  { id: 'advisors', label: 'By Advisor' },
  { id: 'clients', label: 'By Client' },
  { id: 'attention', label: 'Needs Attention' },
];

const CallReports = () => {
  const navigate = useNavigate();
  const cardMode = useReportCardMode();
  const [internalUsers, setInternalUsers] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState('last_90');
  const [tab, setTab] = useState('calls');
  const [search, setSearch] = useState('');
  const [advisorFilter, setAdvisorFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('implementation');
  const [recordingFilter, setRecordingFilter] = useState('all');
  const requestRef = useRef(0);

  useEffect(() => {
    getDocs(collection(db, 'internal_users'))
      .then((snap) => setInternalUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((err) => {
        console.error('Call Reports: failed to load internal users', err);
        setInternalUsers([]);
      });
  }, []);

  const load = useCallback(async () => {
    if (!internalUsers) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      const fetchWindow = getFetchWindow(getTimeframeRange(timeframe, now), now);
      const result = await loadCallReport({ ...fetchWindow, internalUsers });
      if (requestId === requestRef.current) setReport(result);
    } catch (err) {
      console.error('Call Reports: load failed', err);
      if (requestId === requestRef.current) setError(err?.message || 'Could not load call reports');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [internalUsers, timeframe]);

  useEffect(() => {
    load();
  }, [load]);

  // Anchor rolling ranges to the sync time so filtering matches what was fetched.
  const syncedAt = report?.fetchedAt || null;
  const range = useMemo(() => getTimeframeRange(timeframe, syncedAt || new Date()), [timeframe, syncedAt]);
  const rangeIsPast = range.end <= (syncedAt || new Date());

  const advisorOptions = useMemo(
    () => (internalUsers || [])
      .map((u) => ({ id: u.id, name: internalUserName(u) }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [internalUsers]
  );

  // Every filter except timeframe + status (Coming up ignores both).
  const baseCalls = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return report.calls.filter((call) => {
      if (typeFilter === 'implementation' && call.kind !== 'implementation') return false;
      if (advisorFilter !== 'all') {
        const hostId = call.host?.id || null;
        if (advisorFilter === 'unassigned' ? !!hostId : hostId !== advisorFilter) return false;
      }
      if (departmentFilter !== 'all') {
        if (departmentFilter === 'none' ? !!call.department : call.department !== departmentFilter) return false;
      }
      if (q) {
        const haystack = [call.accountName, call.invitee?.name, call.invitee?.email, call.title, call.host?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [report, search, typeFilter, advisorFilter, departmentFilter]);

  const rangeCalls = useMemo(
    () => baseCalls.filter((c) => c.start >= range.start && c.start < range.end),
    [baseCalls, range]
  );

  // A past-only timeframe has nothing upcoming in it, so "Upcoming" falls back to the
  // next 30 days (every fetch covers them).
  const upcomingCalls = useMemo(() => {
    const pool = rangeIsPast ? baseCalls.filter((c) => c.start >= range.end) : rangeCalls;
    return pool.filter((c) => c.status === CALL_STATUS.UPCOMING).sort((a, b) => a.start - b.start);
  }, [baseCalls, rangeCalls, range, rangeIsPast]);

  const visibleCalls = useMemo(() => {
    let calls;
    if (statusFilter === 'all') calls = rangeCalls;
    else if (statusFilter === 'attention') calls = rangeCalls.filter(needsAttention);
    else if (statusFilter === CALL_STATUS.UPCOMING) calls = upcomingCalls;
    else calls = rangeCalls.filter((c) => c.status === statusFilter);
    return recordingFilter === 'all' ? calls : calls.filter((c) => c.recordingStatus === recordingFilter);
  }, [rangeCalls, upcomingCalls, statusFilter, recordingFilter]);

  const comingUp = useMemo(() => {
    const now = new Date();
    const horizon = new Date(now.getTime() + COMING_UP_DAYS * DAY_MS);
    return baseCalls
      .filter((c) => c.status === CALL_STATUS.UPCOMING && c.start >= now && c.start < horizon)
      .sort((a, b) => a.start - b.start)
      .slice(0, 6);
  }, [baseCalls]);

  const stats = useMemo(
    () => ({ ...summarizeCalls(rangeCalls), upcoming: upcomingCalls.length }),
    [rangeCalls, upcomingCalls]
  );
  const advisorRows = useMemo(() => groupCallsByAdvisor(visibleCalls), [visibleCalls]);
  const clientRows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    const includeIdleClients = advisorFilter === 'all' && departmentFilter === 'all' && statusFilter === 'all' && recordingFilter === 'all';
    return groupCallsByClient(visibleCalls, { clients: report.clients, allLogs: report.allLogs, includeIdleClients })
      .filter((row) => !q || String(row.accountName || '').toLowerCase().includes(q) || visibleCalls.some((c) => c.accountId === row.accountId))
      .sort((a, b) => {
        const coldA = !a.nextCall && (a.daysSinceLastCall == null || a.daysSinceLastCall > 30);
        const coldB = !b.nextCall && (b.daysSinceLastCall == null || b.daysSinceLastCall > 30);
        if (coldA !== coldB) return coldA ? -1 : 1;
        return (b.daysSinceLastCall ?? Infinity) - (a.daysSinceLastCall ?? Infinity);
      });
  }, [report, visibleCalls, search, advisorFilter, departmentFilter, statusFilter, recordingFilter]);

  const openClient = useCallback((accountId) => navigate(`/crm/client/${accountId}`), [navigate]);
  const toggleStatus = (status) => setStatusFilter((s) => (s === status ? 'all' : status));
  const toggleRecording = (status) => setRecordingFilter((s) => (s === status ? 'all' : status));
  const hasFilters = search || advisorFilter !== 'all' || departmentFilter !== 'all' || statusFilter !== 'all' || typeFilter !== 'implementation' || recordingFilter !== 'all';

  const initialLoading = loading && !report;

  // The old one-line summary under the stat tiles, now the header KPIs
  const headerStats = report && !initialLoading ? [
    {
      key: 'held',
      icon: CheckCircle2,
      value: stats.heldRate == null ? '—' : `${stats.heldRate}%`,
      label: stats.heldRate == null ? 'No completed or canceled calls yet' : 'held',
    },
    stats.avgMinutes ? { key: 'avg', icon: Clock, value: fmtMinutes(stats.avgMinutes), label: 'avg call' } : null,
    stats.recordingRate != null ? {
      key: 'recorded',
      icon: PlayCircle,
      value: `${stats.recordingRate}%`,
      label: `recorded (${stats.recorded} of ${stats.recorded + stats.notRecorded})`,
      valueClassName: stats.recordingRate < 100 ? GLASS_TEXT.red : '',
    } : null,
    { key: 'logged', icon: ClipboardList, value: fmtHours(stats.loggedHours), label: 'on the Implementation Hours log' },
  ].filter(Boolean) : [];

  const tabOptions = TABS.map((t) => ({
    value: t.id,
    label: (
      <>
        {t.label}
        {t.id === 'attention' && stats.attention > 0 && (
          <span className="ml-1.5 rounded-full bg-black/10 px-1.5 py-0.5 text-[11px] tabular-nums dark:bg-white/[0.12]">
            {stats.attention}
          </span>
        )}
      </>
    ),
  }));

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        backTo="/crm/reports"
        backTitle="Back to reports"
        center={<SlidingPills options={tabOptions} value={tab} onChange={setTab} ariaLabel="Call report view" />}
        title={<>Call<br />Reports</>}
        stats={headerStats}
        controls={(
          <>
            <GlassSelect value={timeframe} onChange={(e) => setTimeframe(e.target.value)} aria-label="Timeframe">
              {TIMEFRAMES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </GlassSelect>
            <GlassAccentButton onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{loading ? 'Syncing…' : 'Sync'}</span>
            </GlassAccentButton>
          </>
        )}
      />

      <div className="space-y-4">
        <div className="glass-rise -mt-4 space-y-3" style={{ animationDelay: '160ms' }}>
          <p className={`text-sm ${MUTED}`}>
            Implementation Hours calls from Calendly, Google Calendar, Fathom and the hours log
            {loading && report && ' · refreshing…'}
          </p>
          <SourceStatusBar sources={report?.sources} fetchedAt={report?.fetchedAt} onConnect={() => navigate('/crm/my-profile')} />
        </div>

        {/* Filters */}
        <GlassCard className="p-4" delay={0}>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1.6fr)_repeat(5,minmax(0,1fr))]">
            <GlassSearch
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search client, invitee, advisor…"
            />
            <GlassSelect className="w-full" value={advisorFilter} onChange={(e) => setAdvisorFilter(e.target.value)} aria-label="Advisor">
              <option value="all">All advisors</option>
              {advisorOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              <option value="unassigned">Unassigned</option>
            </GlassSelect>
            <GlassSelect className="w-full" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} aria-label="Department">
              <option value="all">All departments</option>
              {TASK_TIME_DEPARTMENTS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              <option value="none">No department</option>
            </GlassSelect>
            <GlassSelect className="w-full" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
              <option value="all">All statuses</option>
              {Object.entries(STATUS_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
              <option value="attention">Needs attention</option>
            </GlassSelect>
            <GlassSelect className="w-full" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Call type">
              <option value="implementation">Implementation calls</option>
              <option value="all">All client meetings</option>
            </GlassSelect>
            <GlassSelect className="w-full" value={recordingFilter} onChange={(e) => setRecordingFilter(e.target.value)} aria-label="Recording">
              <option value="all">All recordings</option>
              <option value={RECORDING_STATUS.RECORDED}>Recorded</option>
              <option value={RECORDING_STATUS.NOT_RECORDED}>Not recorded</option>
              <option value={RECORDING_STATUS.PENDING}>Processing</option>
            </GlassSelect>
          </div>
          {hasFilters && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setAdvisorFilter('all');
                  setDepartmentFilter('all');
                  setStatusFilter('all');
                  setTypeFilter('implementation');
                  setRecordingFilter('all');
                }}
                className={`text-xs ${LINK}`}
              >
                Clear filters
              </button>
            </div>
          )}
        </GlassCard>

        {error && (
          <div className="rounded-2xl border border-[#F80707]/20 bg-[#F80707]/[0.06] px-4 py-3 text-center text-sm text-[#B00505] dark:border-[#F80707]/30 dark:bg-[#F80707]/10 dark:text-[#FF6B5E]">
            {error}
          </div>
        )}

        {initialLoading ? (
          <GlassCard className="flex flex-col items-center justify-center gap-4 py-20" delay={80}>
            <BBALoader size={96} />
            <p className={`text-sm ${MUTED}`}>Syncing Calendly, Google Calendar and hours logs…</p>
          </GlassCard>
        ) : report && (
          <>
            {/* Quick stats */}
            <PinnableCard id="quick-stats" title="Call stats" size="wide" placement="corner">
            <GlassCard className="p-4" delay={80}>
              <div className="grid grid-cols-2 gap-3 pr-8 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9">
                <StatTile icon={Phone} label="Total Calls" value={stats.total} tone="gray" onClick={() => setStatusFilter('all')} active={statusFilter === 'all'} />
                <StatTile icon={CheckCircle2} label="Completed" value={stats.completed} tone="green" onClick={() => toggleStatus(CALL_STATUS.COMPLETED)} active={statusFilter === CALL_STATUS.COMPLETED} />
                <StatTile icon={CalendarClock} label={rangeIsPast ? 'Next 30 Days' : 'Upcoming'} value={stats.upcoming} tone="blue" onClick={() => toggleStatus(CALL_STATUS.UPCOMING)} active={statusFilter === CALL_STATUS.UPCOMING} />
                <StatTile icon={XCircle} label="Canceled" value={stats.canceled} tone="red" onClick={() => toggleStatus(CALL_STATUS.CANCELED)} active={statusFilter === CALL_STATUS.CANCELED} />
                <StatTile icon={Repeat} label="Rescheduled" value={stats.rescheduled} tone="yellow" onClick={() => toggleStatus(CALL_STATUS.RESCHEDULED)} active={statusFilter === CALL_STATUS.RESCHEDULED} />
                <StatTile icon={UserX} label="No-shows" value={stats.noShow} tone="orange" onClick={() => toggleStatus(CALL_STATUS.NO_SHOW)} active={statusFilter === CALL_STATUS.NO_SHOW} />
                <StatTile icon={Clock} label="Hours Held" value={fmtHours(stats.heldMinutes / 60)} tone="gray" />
                <StatTile icon={VideoOff} label="Not Recorded" value={stats.notRecorded} tone={stats.notRecorded ? 'red' : 'gray'} onClick={() => toggleRecording(RECORDING_STATUS.NOT_RECORDED)} active={recordingFilter === RECORDING_STATUS.NOT_RECORDED} />
                <StatTile icon={AlertTriangle} label="Needs Attention" value={stats.attention} tone={stats.attention ? 'orange' : 'gray'} onClick={() => setTab('attention')} active={tab === 'attention'} />
              </div>
            </GlassCard>
            </PinnableCard>

            {/* Tab content replays its rise when the tab changes; My view renders every tab's cards */}
            <div key={tab} className="space-y-4">
              {(cardMode || tab === 'calls') && (
                <>
                  {(cardMode || statusFilter === 'all') && (
                    <PinnableCard id="coming-up" title="Coming up" size="wide">
                      <ComingUp calls={comingUp} onOpenClient={openClient} />
                    </PinnableCard>
                  )}
                  <PinnableCard id="calls-table" title="Calls" size="wide">
                    <CallsTable calls={visibleCalls} onOpenClient={openClient} />
                  </PinnableCard>
                </>
              )}
              {(cardMode || tab === 'advisors') && (
                <PinnableCard id="advisor-table" title="By Advisor" size="wide">
                  <AdvisorTable rows={advisorRows} />
                </PinnableCard>
              )}
              {(cardMode || tab === 'clients') && (
                <PinnableCard id="client-table" title="By Client" size="wide">
                  <ClientTable rows={clientRows} onOpenClient={openClient} />
                </PinnableCard>
              )}
              {(cardMode || tab === 'attention') && <AttentionPanel calls={rangeCalls} onOpenClient={openClient} />}
            </div>
          </>
        )}
      </div>
    </GlassPage>
  );
};

export default CallReports;
